"use client";

import type { BlogPost } from "@/data/blog";
import { renderProse, parseInline } from "@/utils/markdown";
import { VideoPlayer } from "./VideoPlayer";
import { ExternalLinkIcon } from "./Button";

// Pull a clean hostname out of a URL for the "where it goes" line (e.g.
// "https://www.pudding.cool/x" -> "pudding.cool"). Returns undefined on garbage.
function hostFromUrl(url: string): string | undefined {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

// =============================================================================
// BLOG CARD
// =============================================================================
// Renders a single blog post card. Variant per post type.
// Two render modes:
//   - compact: for masonry feed (truncated, clickable)
//   - expanded: for fullbleed overlay (full content, no click)

const BLOG_VIOLET = "#A78BFA";

interface BlogCardProps {
  post: BlogPost;
  mode: "compact" | "expanded";
  onClick?: () => void;
  crtEnabled?: boolean;
}

function formatDate(iso: string): string {
  const date = new Date(iso + "T12:00:00");
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function BlogCard({ post, mode, onClick, crtEnabled = true }: BlogCardProps) {
  const isCompact = mode === "compact";
  const textShadow = crtEnabled ? "var(--crt-text-shadow)" : "none";

  // Shared outer container styles. Parent BlogFeed now uses flex columns +
  // gap for spacing, so `mb-4` and `break-inside-avoid` (CSS columns holdovers)
  // are no longer needed.
  const containerClass = isCompact
    ? "w-full text-left group"
    : "w-full text-left";

  const cardClass = [
    "glass-subtle rounded-xl pt-5 px-5 pb-3",
    "transition-all duration-300",
    isCompact && "cursor-pointer group-hover:border-[#A78BFA]/30 group-hover:bg-white/5 hover:-translate-y-0.5",
  ]
    .filter(Boolean)
    .join(" ");

  // Type label pill (used at the top of every card). Posts can carry a finer
  // category (Journal/Dispatch); everything else shows its type (pet → PET,
  // link → LINK).
  const categoryLabel = post.type === "post" && post.category ? post.category : post.type;
  const TypePill = (
    <div className="flex items-center justify-center gap-2 mt-3 select-none">
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-mono font-bold tracking-widest uppercase whitespace-nowrap"
        style={{
          background: BLOG_VIOLET,
          color: "#0b0612",
        }}
      >
        {categoryLabel}
        {/* Links carry the outbound arrow in the tag — it IS the primary action. */}
        {post.type === "link" && <ExternalLinkIcon size={12} />}
      </span>
      <span
        className="text-[10px] font-mono uppercase tracking-widest whitespace-nowrap"
        style={{ color: "rgba(var(--foreground-rgb), 0.45)", textShadow }}
      >
        {formatDate(post.date)}
      </span>
    </div>
  );

  // ---------------------------------------------------------------------------
  // POST (prose)
  // ---------------------------------------------------------------------------
  if (post.type === "post") {
    const proseClass = isCompact
      ? "text-sm leading-relaxed line-clamp-5"
      : "text-base leading-relaxed";

    return (
      <button
        type="button"
        className={containerClass}
        onClick={onClick}
        disabled={!isCompact}
      >
        <div
          className={cardClass}
          style={{ color: "rgba(var(--foreground-rgb), 0.8)", textShadow }}
        >
          {post.title && (
            <h3
              className="font-headline text-lg font-bold mb-1 leading-tight"
              style={{ color: "rgba(var(--foreground-rgb), 1)" }}
            >
              {post.title}
            </h3>
          )}
          {post.subtitle && (
            <p
              className="text-xs font-mono mb-3 leading-snug"
              style={{ color: "rgba(var(--foreground-rgb), 0.45)", textShadow }}
            >
              {post.subtitle}
            </p>
          )}
          <div className={proseClass}>{renderProse(post.body, post.id)}</div>
          {TypePill}
        </div>
      </button>
    );
  }

  // ---------------------------------------------------------------------------
  // LINK (a cool thing seen or sent to V)
  // ---------------------------------------------------------------------------
  // The whole card is a real anchor: clicking opens the destination in a new
  // tab. We intentionally ignore `onClick` (the feed's expand handler) so links
  // open directly. The lightbox still renders links via prev/next — that mapping
  // lives in BlogFeed, not here. Note is plain text here (the card is itself a
  // link, so we can't nest markdown links); the lightbox shows the rich note.
  if (post.type === "link") {
    const host = hostFromUrl(post.url);
    return (
      <a
        href={post.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${containerClass} block`}
      >
        <div
          className={cardClass}
          style={{ color: "rgba(var(--foreground-rgb), 0.8)", textShadow }}
        >
          {post.image && (
            <div className="rounded-lg overflow-hidden mb-3">
              <img
                src={post.image}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-full h-auto block aspect-[16/9] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
            </div>
          )}
          <h3
            className="font-headline text-lg font-bold leading-tight mb-1"
            style={{ color: "rgba(var(--foreground-rgb), 1)" }}
          >
            {post.title}
          </h3>
          {host && (
            <p
              className="text-xs font-mono mb-2 leading-snug truncate"
              style={{ color: BLOG_VIOLET, textShadow }}
            >
              {host}
            </p>
          )}
          {post.note && (
            <p className="text-sm leading-relaxed line-clamp-4">{post.note}</p>
          )}
          {TypePill}
        </div>
      </a>
    );
  }

  // ---------------------------------------------------------------------------
  // PET (image or video + caption)
  // ---------------------------------------------------------------------------
  // Layout: Asset first, then caption, then tag+date (matches lightbox IA)
  //
  // Media flows to its natural aspect ratio -- no 4/3 crop, because the photos
  // have already been curated and we don't want to chop heads or tails off
  // anyone. Masonry (CSS columns) handles the variable heights.
  //
  // Compact mode always shows the poster image (post.image) -- we never mount
  // a <video> in the masonry grid because dozens of decoders fighting for GPU
  // is how laptops get hot. When post.video exists, we overlay a small film
  // badge in the top-right so it's clear this card will play video on expand.
  //
  // Expanded mode swaps to VideoPlayer when post.video is set. Preload bumps
  // from "none" to "metadata" once expanded so the file starts buffering as
  // soon as the card opens.
  if (post.type === "pet") {
    const hasVideo = !!post.video;
    const showVideoPlayer = !isCompact && hasVideo;

    // Full-bleed pet card: asset fills the card, metadata floats on top as
    // backdrop-blurred chips. No inner padding -- the photo/video IS the tile.
    // - top-left:  video indicator (only when asset is a video poster)
    // - top-right: tag + date chip cluster
    // - bottom:    caption on gradient scrim (only when caption exists)
    //
    // Prose posts keep the padded glass card because the card IS their content.
    const petContainerClass = [
      "group relative w-full text-left overflow-hidden rounded-xl",
      "transition-all duration-300",
      isCompact && "cursor-pointer hover:-translate-y-0.5",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        type="button"
        className={petContainerClass}
        onClick={onClick}
        disabled={!isCompact}
        style={{ color: "rgba(var(--foreground-rgb), 0.8)", textShadow }}
      >
        {/* Asset -- full bleed, sets the card's height via natural aspect ratio */}
        {!showVideoPlayer ? (
          <img
            src={post.image}
            alt={post.alt}
            loading="lazy"
            decoding="async"
            className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
          />
        ) : (
          <VideoPlayer
            src={post.video!}
            poster={post.image}
            alt={post.alt}
            caption={post.caption}
            preload="metadata"
            className="w-full h-auto block"
          />
        )}

        {/* Compact-only overlays: only shown in masonry feed, not expanded view */}
        {isCompact && (
          <>
            {/* Bottom info stack -- social-media style footer, center aligned.
                Caption on top, tag row (type + optional video + date) below.
                Always rendered so every tile has consistent identity. Gradient
                height adapts: taller when caption present. */}
            <div
              className={`absolute inset-x-0 bottom-0 px-3 pb-2.5 pointer-events-none flex flex-col items-center text-center ${
                post.caption ? "pt-12" : "pt-8"
              }`}
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 100%)",
              }}
            >
              {post.caption && (
                <p
                  className="text-xs leading-snug line-clamp-2 text-white mb-1.5 max-w-full"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
                >
                  {parseInline(post.caption, post.id)}
                </p>
              )}
              {/* Meta: tag(s) + date. Layout depends on whether a Video chip
                  is present:
                  - No video: PET and date sit inline (fits on one line).
                  - With video: PET + VIDEO stack on line 1, date drops to
                    line 2 so it never wraps mid-word on narrow cards.
                  Date always gets whitespace-nowrap so "MAR 29, 2026" stays
                  intact as a single unit regardless of layout. */}
              <div className="flex flex-col items-center gap-1 select-none">
                <div className="flex items-center justify-center gap-1.5">
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-mono font-bold tracking-widest uppercase whitespace-nowrap"
                    style={{
                      background: BLOG_VIOLET,
                      color: "#0b0612",
                    }}
                  >
                    {post.type}
                  </span>
                  {hasVideo && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-mono font-bold tracking-widest uppercase whitespace-nowrap"
                      style={{
                        background: "rgba(255,255,255,0.92)",
                        color: "#0b0612",
                      }}
                      aria-hidden="true"
                    >
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M4 3a1.5 1.5 0 0 1 2.25-1.3l15 9a1.5 1.5 0 0 1 0 2.6l-15 9A1.5 1.5 0 0 1 4 21V3z" />
                      </svg>
                      Video
                    </span>
                  )}
                  {!hasVideo && (
                    <span
                      className="text-[10px] font-mono uppercase tracking-widest text-white/80 whitespace-nowrap"
                      style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
                    >
                      {formatDate(post.date)}
                    </span>
                  )}
                </div>
                {hasVideo && (
                  <span
                    className="text-[10px] font-mono uppercase tracking-widest text-white/80 whitespace-nowrap"
                    style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
                  >
                    {formatDate(post.date)}
                  </span>
                )}
              </div>
            </div>

            {/* Hover ring -- subtle violet border reveal on hover */}
            <div
              className="absolute inset-0 rounded-xl pointer-events-none transition-opacity duration-300 opacity-0 group-hover:opacity-100"
              style={{ border: `1px solid ${BLOG_VIOLET}55` }}
              aria-hidden="true"
            />
          </>
        )}
      </button>
    );
  }

  // Post/pet are exhaustive for BlogPost's discriminated union -- this return
  // is unreachable but satisfies TS's "all paths return" check.
  return null;
}
